/**
 * Merge Token Registry (AB#2464771)
 *
 * Single source of truth for the merge-field / field-inserter functionality.
 *
 * Previously the codebase had two divergent token registries
 * (`ALL_MERGE_TOKENS` in customerTermsService — 8 tokens — used by the Terms &
 * Conditions and Section editors, and `EMAIL_MERGE_TOKENS` in
 * EmailTemplateEditor — 17 tokens). This module merges them into one keyed
 * list and adds a per-token `scopes` flag so each screen can select the subset
 * relevant to its context (e.g. `{client.email}` is offered in the email
 * editor but not the T&C editor).
 *
 * It also provides:
 *   - the single set of category labels used by every screen,
 *   - a single sample-data set + resolver used for every preview,
 *   - fuzzy matching used by both the side panel and the autocomplete dropdown,
 *   - token validation (unknown / malformed detection, empty-required checks).
 */

// ============================================================================
// TYPES
// ============================================================================

export type MergeTokenCategory = 'client' | 'invoice' | 'project' | 'company';

/**
 * A screen / context in which a token may be offered. Screens ask the registry
 * for the tokens in their scope via {@link getMergeTokensForScope}.
 */
export type MergeTokenScope = 'email' | 'terms' | 'notes' | 'section';

/** Display format hint — governs how sample/real values are formatted. */
export type MergeTokenFormat = 'text' | 'email' | 'phone' | 'number' | 'currency' | 'date';

export interface MergeTokenDef {
  /** The raw token string inserted into content, e.g. `{client.name}`. */
  token: string;
  /** Friendly bracketed label used as the chip text, e.g. `[Client Name]`. */
  label: string;
  /** Category used to group tokens in the picker. */
  category: MergeTokenCategory;
  /** One-line human description of what this token resolves to. */
  description: string;
  /** Value format hint (used for preview formatting + display metadata). */
  format: MergeTokenFormat;
  /** Contexts in which this token is offered. */
  scopes: MergeTokenScope[];
  /**
   * When true the field is expected to have a value before an email is sent;
   * `validateRequiredTokens` warns when the resolved value is empty.
   */
  required?: boolean;
}

// ============================================================================
// CATEGORY DISPLAY
// ============================================================================

/** Standardised category labels — used by every screen. */
export const CATEGORY_LABELS: Record<MergeTokenCategory, string> = {
  client: 'Client',
  invoice: 'Invoice',
  project: 'Project',
  company: 'Company',
};

/** Fixed display order for categories in the picker. */
export const CATEGORY_ORDER: MergeTokenCategory[] = ['client', 'invoice', 'project', 'company'];

// ============================================================================
// THE REGISTRY
// ============================================================================

const ALL: MergeTokenScope[] = ['email', 'terms', 'notes', 'section'];
const INVOICE_TEMPLATE: MergeTokenScope[] = ['terms', 'notes', 'section'];
const EMAIL_ONLY: MergeTokenScope[] = ['email'];

/**
 * The single merge-token list. Ordered by category then significance.
 */
export const MERGE_TOKENS: MergeTokenDef[] = [
  // ── Client ────────────────────────────────────────────────────────────────
  {
    token: '{client.name}',
    label: '[Client Name]',
    category: 'client',
    description: 'The name of the client being invoiced',
    format: 'text',
    scopes: ALL,
  },
  {
    token: '{client.contactName}',
    label: '[Contact Name]',
    category: 'client',
    description: 'Primary contact first + last name',
    format: 'text',
    scopes: EMAIL_ONLY,
  },
  {
    token: '{client.contactFirstName}',
    label: '[Contact First Name]',
    category: 'client',
    description: 'Primary contact first name only',
    format: 'text',
    scopes: EMAIL_ONLY,
  },
  {
    token: '{client.email}',
    label: '[Client Email]',
    category: 'client',
    description: 'Primary billing email address',
    format: 'email',
    scopes: EMAIL_ONLY,
    required: true,
  },
  {
    token: '{client.phone}',
    label: '[Client Phone]',
    category: 'client',
    description: 'Primary contact phone number',
    format: 'phone',
    scopes: EMAIL_ONLY,
  },
  {
    token: '{client.accountNumber}',
    label: '[Account Number]',
    category: 'client',
    description: 'Client account reference number',
    format: 'text',
    scopes: EMAIL_ONLY,
  },
  {
    token: '{client.paymentTermsDays}',
    label: '[Payment Terms Days]',
    category: 'client',
    description: 'Number of days until payment is due (e.g. 30)',
    format: 'number',
    scopes: ALL,
  },
  {
    token: '{client.paymentTermsDescription}',
    label: '[Payment Terms Description]',
    category: 'client',
    description: 'Human-readable payment terms (e.g. Net 30 Days)',
    format: 'text',
    scopes: INVOICE_TEMPLATE,
  },
  {
    token: '{client.paymentTermsCode}',
    label: '[Payment Terms Code]',
    category: 'client',
    description: 'Short payment terms code (e.g. NET30)',
    format: 'text',
    scopes: INVOICE_TEMPLATE,
  },
  // ── Invoice ─────────────────────────────────────────────────────────────────
  {
    token: '{invoice.number}',
    label: '[Invoice Number]',
    category: 'invoice',
    description: 'Invoice reference number (e.g. INV-2024-042)',
    format: 'text',
    scopes: EMAIL_ONLY,
  },
  {
    token: '{invoice.date}',
    label: '[Invoice Date]',
    category: 'invoice',
    description: 'The date the invoice was issued',
    format: 'date',
    scopes: ALL,
  },
  {
    token: '{invoice.dueDate}',
    label: '[Due Date]',
    category: 'invoice',
    description: 'The date payment is due',
    format: 'date',
    scopes: ALL,
  },
  {
    token: '{invoice.total}',
    label: '[Invoice Total]',
    category: 'invoice',
    description: 'Total invoice amount including VAT',
    format: 'currency',
    scopes: EMAIL_ONLY,
  },
  {
    token: '{invoice.amountDue}',
    label: '[Amount Due]',
    category: 'invoice',
    description: 'Remaining balance due (after credits)',
    format: 'currency',
    scopes: EMAIL_ONLY,
  },
  // ── Project ───────────────────────────────────────────────────────────────
  {
    token: '{project.name}',
    label: '[Project Name]',
    category: 'project',
    description: 'The name of the project being invoiced',
    format: 'text',
    scopes: ALL,
  },
  {
    token: '{project.code}',
    label: '[Project Code]',
    category: 'project',
    description: 'Internal project reference code',
    format: 'text',
    scopes: EMAIL_ONLY,
  },
  // ── Company ─────────────────────────────────────────────────────────────────
  {
    token: '{company.name}',
    label: '[Company Name]',
    category: 'company',
    description: 'Your company name as entered in Company Details',
    format: 'text',
    scopes: ALL,
  },
  {
    token: '{company.senderName}',
    label: '[Sender Name]',
    category: 'company',
    description: 'Name of the person sending the invoice',
    format: 'text',
    scopes: EMAIL_ONLY,
  },
  {
    token: '{company.senderEmail}',
    label: '[Sender Email]',
    category: 'company',
    description: 'Reply-to email address for responses',
    format: 'email',
    scopes: EMAIL_ONLY,
  },
];

// A quick lookup by raw token string.
const TOKEN_BY_STRING = new Map<string, MergeTokenDef>(MERGE_TOKENS.map((t) => [t.token, t]));

// ============================================================================
// SELECTION HELPERS
// ============================================================================

/** All tokens (unfiltered). */
export function getAllMergeTokens(): MergeTokenDef[] {
  return MERGE_TOKENS;
}

/** Tokens available in a given screen/context. */
export function getMergeTokensForScope(scope: MergeTokenScope): MergeTokenDef[] {
  return MERGE_TOKENS.filter((t) => t.scopes.includes(scope));
}

/** Look up a single token definition by its raw string. */
export function getMergeToken(token: string): MergeTokenDef | undefined {
  return TOKEN_BY_STRING.get(token);
}

/** Friendly display name with the surrounding brackets stripped. */
export function tokenDisplayName(t: Pick<MergeTokenDef, 'label'>): string {
  return t.label.replace(/^\[|\]$/g, '');
}

// ============================================================================
// FUZZY MATCHING
// Shared by the side panel search box and the autocomplete dropdown so both
// filter and rank identically.
// ============================================================================

/**
 * Returns a match score for `query` against `target`, or `null` for no match.
 * Higher is better. Contiguous / prefix matches score higher than scattered
 * subsequence matches.
 */
export function fuzzyScore(query: string, target: string): number | null {
  const q = query.trim().toLowerCase();
  const t = target.toLowerCase();
  if (q === '') return 1;
  if (t.includes(q)) {
    // Contiguous substring — big bonus, extra for prefix.
    return 1000 - t.indexOf(q) + (t.startsWith(q) ? 500 : 0);
  }
  // Subsequence match — every query char appears in order.
  let score = 0;
  let ti = 0;
  let prevMatch = -2;
  for (let qi = 0; qi < q.length; qi++) {
    const ch = q[qi];
    let found = -1;
    for (; ti < t.length; ti++) {
      if (t[ti] === ch) {
        found = ti;
        break;
      }
    }
    if (found === -1) return null;
    // Reward adjacency.
    score += found === prevMatch + 1 ? 5 : 1;
    prevMatch = found;
    ti = found + 1;
  }
  return score;
}

/**
 * Filters + ranks tokens for a query, matching against the display name, raw
 * token and description. Returns tokens best-match first; an empty query keeps
 * the registry order.
 */
export function filterTokens<T extends MergeTokenDef>(tokens: T[], query: string): T[] {
  const q = query.trim();
  if (q === '') return tokens;
  const scored: { token: T; score: number }[] = [];
  for (const token of tokens) {
    const nameScore = fuzzyScore(q, tokenDisplayName(token));
    const tokenScore = fuzzyScore(q, token.token);
    const descScore = fuzzyScore(q, token.description);
    const best = Math.max(
      nameScore ?? -Infinity,
      // The raw token / description are weaker signals than the name.
      tokenScore != null ? tokenScore * 0.6 : -Infinity,
      descScore != null ? descScore * 0.3 : -Infinity,
    );
    if (best > -Infinity) scored.push({ token, score: best });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.map((s) => s.token);
}

// ============================================================================
// SAMPLE DATA + RESOLUTION
// The single sample-data set and resolver used by every preview.
// ============================================================================

/** Sample values for previews when no real client/invoice record is loaded. */
export const SAMPLE_TOKEN_DATA: Readonly<Record<string, string>> = {
  '{client.name}': 'Acme Corporation Ltd',
  '{client.contactName}': 'James Whitfield',
  '{client.contactFirstName}': 'James',
  '{client.email}': 'james@acme.com',
  '{client.phone}': '+44 20 7123 4567',
  '{client.accountNumber}': 'ACC-00042',
  '{client.paymentTermsDays}': '30',
  '{client.paymentTermsDescription}': 'Net 30 Days',
  '{client.paymentTermsCode}': 'NET30',
  '{invoice.number}': 'INV-2024-042',
  '{invoice.date}': '15 Jan 2025',
  '{invoice.dueDate}': '14 Feb 2025',
  '{invoice.total}': '£12,450.00',
  '{invoice.amountDue}': '£12,450.00',
  '{project.name}': 'Website Redesign',
  '{project.code}': 'PRJ-0042',
  '{company.name}': 'Horizon Group Ltd',
  '{company.senderName}': 'Sarah Johnson',
  '{company.senderEmail}': 'sarah@horizongroup.com',
};

/**
 * Replaces raw `{token}` occurrences in a plain-text string with values from
 * `data`. Tokens without a (non-empty) value are left untouched so previews
 * still show a readable placeholder.
 */
export function resolveTokensInText(
  text: string,
  data: Record<string, string> = SAMPLE_TOKEN_DATA,
): string {
  if (!text) return text;
  let out = text;
  for (const { token } of MERGE_TOKENS) {
    const value = data[token];
    if (value != null && value !== '') {
      out = out.replaceAll(token, value);
    }
  }
  return out;
}

/**
 * Resolves an HTML string produced by MergeFieldEditor for preview:
 *   1. chip spans (`<span data-merge-token="{token}">label</span>`) → value
 *   2. any remaining raw `{token}` strings → value
 *   3. bracketed friendly labels (`[Client Name]`) → value (legacy content)
 *
 * Tokens whose value is empty/missing keep their friendly placeholder so the
 * preview never shows a raw `{token}` gap.
 */
export function resolveTokensInHtml(
  html: string,
  data: Record<string, string> = SAMPLE_TOKEN_DATA,
): string {
  if (!html) return html;

  // 1. Chip spans → resolved value (or friendly label when empty/unknown).
  let out = html.replace(
    /<span[^>]*data-merge-token="([^"]+)"[^>]*>([^<]*)<\/span>/g,
    (_full, token: string, label: string) => {
      const value = data[token];
      if (value != null && value !== '') return value;
      const def = TOKEN_BY_STRING.get(token);
      return label || (def ? tokenDisplayName(def) : token);
    },
  );

  // 2 + 3. Remaining raw tokens and legacy bracket labels.
  for (const def of MERGE_TOKENS) {
    const value = data[def.token];
    if (value != null && value !== '') {
      out = out.replaceAll(def.token, value);
      out = out.replaceAll(def.label, value);
    }
  }
  return out;
}

// ============================================================================
// VALIDATION
// ============================================================================

export type TokenIssueType = 'unknown' | 'malformed';

export interface TokenIssue {
  type: TokenIssueType;
  /** The raw offending text, e.g. `{client.naem}` or `{unclosed`. */
  text: string;
}

const WELL_FORMED_TOKEN = /^\{[a-zA-Z][\w]*(?:\.[a-zA-Z][\w]*)+\}$/;

/**
 * Scans content for merge-token problems:
 *   - `malformed`: an opening `{` that is not a valid `{scope.field}` token
 *   - `unknown`: a well-formed token that is not in the registry
 *
 * Chip spans are ignored — their token attribute is authored by the editor and
 * is always valid. Only free-typed tokens are validated.
 */
export function findTokenIssues(content: string): TokenIssue[] {
  if (!content) return [];
  // Strip chip spans so their data-merge-token attribute isn't double-counted.
  const stripped = content.replace(
    /<span[^>]*data-merge-token="[^"]*"[^>]*>[^<]*<\/span>/g,
    '',
  );
  const issues: TokenIssue[] = [];
  const seen = new Set<string>();
  // Match a `{` up to the next `{` or `}` (so unclosed braces are caught).
  const re = /\{[^{}]*\}?/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(stripped)) !== null) {
    const raw = m[0];
    if (!raw.includes('.') && raw === '{') continue;
    if (seen.has(raw)) continue;
    seen.add(raw);
    if (!raw.endsWith('}') || !WELL_FORMED_TOKEN.test(raw)) {
      // Ignore stray single braces that aren't token-like.
      if (/\{[^}]/.test(raw) || raw.startsWith('{')) {
        if (raw.length > 1) issues.push({ type: 'malformed', text: raw });
      }
      continue;
    }
    if (!TOKEN_BY_STRING.has(raw)) {
      issues.push({ type: 'unknown', text: raw });
    }
  }
  return issues;
}

export interface RequiredTokenWarning {
  token: MergeTokenDef;
}

/**
 * Returns required tokens (for the given scope) that are used in `content` but
 * resolve to an empty value in `data` — used to warn before sending.
 */
export function validateRequiredTokens(
  content: string,
  data: Record<string, string>,
  scope: MergeTokenScope,
): RequiredTokenWarning[] {
  if (!content) return [];
  const warnings: RequiredTokenWarning[] = [];
  for (const def of MERGE_TOKENS) {
    if (!def.required || !def.scopes.includes(scope)) continue;
    const used = content.includes(def.token) || content.includes(def.label);
    if (!used) continue;
    const value = data[def.token];
    if (value == null || value === '') warnings.push({ token: def });
  }
  return warnings;
}
